/* eslint-disable max-lines */
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cx } from "../../../utils/classnames";
import Animation from "../Animation";
import SpinnerLoader from "../SpinnerLoader";
import Input from "./Input";
import { SelectOption, SelectProps } from "./Input.types";

const normalizeString = (str: string): string =>
  str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const SelectInput = ({
  label,
  className,
  options,
  selectedId,
  error,
  loading,
  placeholder,
  onOptionSelect,
  isOpen: externalIsOpen,
  onClick,
  inputRef: externalInputRef,
  onEnter,
  ...props
}: SelectProps & { inputRef?: React.RefObject<HTMLInputElement>; onEnter?: () => void }) => {
  const [isOpen, setIsOpen] = useState(externalIsOpen || false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);
  const [inputMode, setInputMode] = useState<"selection" | "search">("selection");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<HTMLInputElement>(null);
  const activeInputRef = externalInputRef || internalInputRef;

  useEffect(() => {
    if (selectedId) {
      const found = options.find((opt) => opt.id === selectedId) || null;
      setSelectedOption(found);
      if (found) {
        setInputMode("selection");
      }
    } else {
      setSelectedOption(null);
    }
  }, [selectedId, options]);

  useEffect(() => {
    if (externalIsOpen !== undefined) setIsOpen(externalIsOpen);
  }, [externalIsOpen]);

  const filteredOptions = useMemo(() => {
    const firstId = options.length > 0 ? String(options[0].id) : "";
    const isTimeList = /^\d{2}:\d{2}$/.test(firstId);
    const isValidTimeInput = /^\d{1,2}:\d{2}$/.test(searchTerm);

    if (isTimeList && isValidTimeInput) {
      const [h, m] = searchTerm.split(":").map(Number);
      const threshold = h * 60 + m;

      return options.filter((o) => {
        const [oh, om] = String(o.id).split(":").map(Number);
        return oh * 60 + om >= threshold;
      });
    }

    return searchTerm
      ? options.filter((o) =>
          normalizeString(o.name).includes(normalizeString(searchTerm))
        )
      : options;
  }, [options, searchTerm]);

  useEffect(() => {
    const onOutside = (e: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    if (isOpen) document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [isOpen]);

  useEffect(() => {
    if (highlightedIndex >= 0 && isOpen) {
      const el = selectRef.current?.querySelectorAll<HTMLDivElement>(".option-item")[
        highlightedIndex
      ];
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [highlightedIndex, isOpen]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSearchTerm("");
  }, []);

  const handleOptionSelect = useCallback(
    (opt: SelectOption) => {
      setSelectedOption(opt);
      setInputMode("selection");
      closeDropdown();
      if (props.onChange) {
        props.onChange({
          target: { name: props.name, value: opt.id }
        } as React.ChangeEvent<HTMLSelectElement>);
      }
      onOptionSelect?.(opt);
      setSearchTerm("");
    },
    [closeDropdown, onOptionSelect, props]
  );

  const handleClear = useCallback(() => {
    setSelectedOption(null);
    setInputMode("search");
    setIsOpen(true);
    setSearchTerm("");
    if (props.onChange) {
      props.onChange({
        target: { name: props.name, value: "" }
      } as React.ChangeEvent<HTMLSelectElement>);
    }
    onOptionSelect?.(null as any);
  }, [onOptionSelect, props]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const len = filteredOptions.length;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setIsOpen(true);
          setHighlightedIndex((i) => (i < len - 1 ? i + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setIsOpen(true);
          setHighlightedIndex((i) => (i > 0 ? i - 1 : len - 1));
          break;
        case "Enter":
          e.preventDefault();
          let selected = null;
          if (!isOpen) {
             if(!searchTerm) {
                 setIsOpen(true);
                 setHighlightedIndex(0);
                 return;
             }
          }
          if (highlightedIndex >= 0 && highlightedIndex < len) {
            selected = filteredOptions[highlightedIndex];
          } else if (searchTerm) {
            const exactMatch = options.find(
              (o) =>
                o.name.toLowerCase() === searchTerm.toLowerCase() ||
                String(o.id) === searchTerm
            );
            selected = exactMatch || { id: searchTerm, name: searchTerm };
          } else if (len === 1) {
            selected = filteredOptions[0];
          }

          if (selected) {
            handleOptionSelect(selected);
            if (onEnter) {
                setTimeout(() => onEnter(), 50);
            }
          }
          break;
        case "Escape":
          e.preventDefault();
          closeDropdown();
          break;
        case "Backspace":
        case "Delete":
          if (selectedOption && !searchTerm) {
            e.preventDefault();
            handleClear();
          }
          break;
      }
    },
    [
      filteredOptions,
      highlightedIndex,
      isOpen,
      selectedOption,
      searchTerm,
      options,
      handleOptionSelect,
      closeDropdown,
      handleClear,
      onEnter
    ]
  );

  const inputValue =
    inputMode === "search"
      ? searchTerm
      : selectedOption
      ? selectedOption.name
      : "";

  return (
    <div ref={selectRef} className="relative">
      <Input
        ref={activeInputRef}
        id={props.id}
        name={props.name}
        label={label}
        className={`relative ${className}`}
        placeholder={placeholder}
        error={error}
        value={inputValue}
        onClick={() => {
          onClick ? onClick() : setIsOpen((o) => !o);
          setInputMode("search");
        }}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setInputMode("search");
          setIsOpen(true);
          setHighlightedIndex(-1);
          props.onChange?.(e);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (!selectedOption) setInputMode("search");
        }}
        after={
          <span className="pr-2 flex items-center">
            {loading && <SpinnerLoader className="mr-3" size={20} />}
            <ChevronDownIcon
              className={cx([
                "h-3 w-3 transform duration-150",
                isOpen ? "rotate-180" : "rotate-0"
              ])}
            />
          </span>
        }
      />

      {isOpen && filteredOptions.length > 0 && (
        <Animation type="fade-top" show duration={300} leaveInDom>
          <div
            className={cx([
              "absolute z-[999] mt-2 w-full max-h-80 overflow-y-auto rounded-md bg-white",
              loading ? "border-0" : "border"
            ])}
          >
            {filteredOptions.map((opt, idx) => (
              <div
                key={opt.id}
                className={cx([
                  "option-item flex cursor-pointer items-center justify-between p-3 text-sm rounded",
                  highlightedIndex === idx && "bg-primary bg-opacity-5",
                  selectedOption?.id === opt.id && "text-primary",
                  "hover:bg-primary hover:bg-opacity-5"
                ])}
                tabIndex={0}
                onClick={() => handleOptionSelect(opt)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleOptionSelect(opt);
                    if(onEnter) setTimeout(() => onEnter(), 50);
                  }
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {opt.name}
                {selectedOption?.id === opt.id && (
                  <CheckIcon className="w-4 h-4 text-primary" />
                )}
              </div>
            ))}
          </div>
        </Animation>
      )}
    </div>
  );
};

export default SelectInput;
